import React, { lazy } from "react";
import { Navigate } from "react-router-dom";

import Landing from "../pages/Landing";
import ScanPage from "../pages/Scan";
import ContractPage from "../pages/Contract";
import AtlasPage from "../pages/Atlas";
import MethodologyPage from "../pages/Methodology";
import MethodologyVersionPage from "../pages/MethodologyVersion";
import ReportPage from "../pages/Report";
import Audit from "../pages/Audit";
import AuditPreview from "../pages/AuditPreview";
import PricingAudits from "../pages/PricingAudits";
import AuditTalkToSales from "../pages/AuditTalkToSales";
import AuditCheckoutSuccess from "../pages/AuditCheckoutSuccess";
import DemoCheckoutSuccess from "../pages/DemoCheckoutSuccess";
import DemoShareRedeem from "../pages/DemoShareRedeem";
import Unsubscribe from "../pages/Unsubscribe";
import VerifyWebhook from "../pages/VerifyWebhook";
import SdkSamples from "../pages/SdkSamples";
import WhatsNew from "../pages/WhatsNew";
import SignIn from "../pages/SignIn";
import { ForgotPassword, ResetPassword } from "../pages/PasswordReset";
// Phase 9 (iter269.20m) — Finance & Partnership Deck. Lazy: only loaded
// when the URL is opened (no SEO, no prefetch — the URL itself is the secret).
const Deck = lazy(() =>
  import(/* webpackChunkName: "deck" */ "../pages/Deck")
);
const PublicDeck = lazy(() =>
  import(/* webpackChunkName: "public-deck" */ "../pages/PublicDeck")
);

import Terms from "../pages/legal/Terms";
import Privacy from "../pages/legal/Privacy";
import CookiesPolicy from "../pages/legal/Cookies";
import Security from "../pages/legal/Security";

// Marketing-funnel pages: lazy + prefetched. webpack injects
// <link rel="prefetch"> for these chunks into the landing HTML, so
// the browser fetches them during idle after the homepage renders.
// Result: tiny first-paint AND instant navigation to the highest-
// conversion next-steps (FreeScan, Pricing).
// Iter273.8 — Beta items #28 + #30 + #31: public 12-step Quickstart
// Guide on Self-Serve. Lazy because most visitors won't see it on the
// landing page — they reach it via the Self-Serve MegaMenu link.
const PublicQuickstart = lazy(() =>
  import(/* webpackChunkName: "public-quickstart" */ "../pages/PublicQuickstart")
);

const FreeScan = lazy(() =>
  import(/* webpackChunkName: "freescan", webpackPrefetch: true */ "../pages/FreeScan")
);
const ScoreMyContractRedirect = lazy(() =>
  import(/* webpackChunkName: "score-my-contract" */ "../pages/ScoreMyContractRedirect")
);
const ScanResult = lazy(() =>
  import(/* webpackChunkName: "scan-result" */ "../pages/ScanResult")
);
const Pricing = lazy(() =>
  import(/* webpackChunkName: "pricing", webpackPrefetch: true */ "../pages/Pricing")
);
// Verify is shared externally on social — prefetch so incoming links
// from LinkedIn/Twitter previews feel instant.
const VerifyPage = lazy(() =>
  import(/* webpackChunkName: "verify", webpackPrefetch: true */ "../pages/Verify")
);

// Public docs platform (Phase 2b). Lazy + prefetched on landing so the IA
// front door + article reader are instant after the homepage paints.
const DocsLanding = lazy(() =>
  import(/* webpackChunkName: "docs-landing", webpackPrefetch: true */ "../pages/docs/DocsLanding")
);
const DocsArticle = lazy(() =>
  import(/* webpackChunkName: "docs-article", webpackPrefetch: true */ "../pages/docs/DocsArticle")
);

// Phase 8.1 — Help Center hub.
const HelpCenter = lazy(() =>
  import(/* webpackChunkName: "help-center", webpackPrefetch: true */ "../pages/HelpCenter")
);
const HelpCategory = lazy(() =>
  import(/* webpackChunkName: "help-category" */ "../pages/HelpCategory")
);
// Phase 8.4 — Public Community Pulse / Trends.
const PublicTrends = lazy(() =>
  import(/* webpackChunkName: "public-trends" */ "../pages/PublicTrends")
);
// Iter270.1 — Public momentum dashboard (anonymized scan velocity).
const Momentum = lazy(() =>
  import(/* webpackChunkName: "momentum" */ "../pages/Momentum")
);
// Iter270.1 — Public scan comparator (two scans, side-by-side).
const Compare = lazy(() =>
  import(/* webpackChunkName: "compare" */ "../pages/Compare")
);
// Iter276 — Programmatic SEO chain landing page (/chains/:slug).
const ChainLanding = lazy(() =>
  import(/* webpackChunkName: "chain-landing" */ "../pages/ChainLanding")
);
// Phase 4 — Public Trust & Compliance page.
const Trust = lazy(() =>
  import(/* webpackChunkName: "public-trust", webpackPrefetch: true */ "../pages/Trust")
);
const TrustPolicyView = lazy(() =>
  import(/* webpackChunkName: "public-trust-policy" */ "../pages/Trust").then((m) => ({ default: m.TrustPolicyView }))
);
// Phase 12.6 — Standalone Threat Model page.
const ThreatModel = lazy(() =>
  import(/* webpackChunkName: "public-threat-model" */ "../pages/ThreatModel")
);
// Phase 12.6 — Sister Brands hub.
const SisterBrands = lazy(() =>
  import(/* webpackChunkName: "public-sister-brands" */ "../pages/SisterBrands")
);
// Phase 15.1 / M5 — Q-Day Tax Clock + embeddable widget.
const QDayClock = lazy(() =>
  import(/* webpackChunkName: "qday-clock", webpackPrefetch: true */ "../pages/QDayClock")
);
const QDayCounterWidget = lazy(() =>
  import(/* webpackChunkName: "qday-widget" */ "../pages/QDayCounterWidget")
);
// Phase 12.7c — Public Release log + Atom/RSS feeds.
const Releases = lazy(() =>
  import(/* webpackChunkName: "public-releases" */ "../pages/Releases")
);
// Phase 12.5 — Trust Center audit-updates unsubscribe landing.
const TrustAuditUpdatesUnsubscribe = lazy(() =>
  import(/* webpackChunkName: "public-trust-unsub" */ "../pages/TrustAuditUpdatesUnsubscribe")
);
// Phase 5 Sprint 4+5 — Public Threat Radar (marketing weapon).
const ThreatRadar = lazy(() =>
  import(/* webpackChunkName: "threat-radar", webpackPrefetch: true */ "../pages/ThreatRadar")
);
const ThreatRadarEmbed = lazy(() =>
  import(/* webpackChunkName: "threat-radar-embed" */ "../pages/ThreatRadarEmbed")
);
const ThreatRadarStats = lazy(() =>
  import(/* webpackChunkName: "threat-radar-stats" */ "../pages/ThreatRadarStats")
);
// Iter203 — Customer-curated public radars (pinnable from security.txt).
const CustomerThreatRadarPublic = lazy(() =>
  import(/* webpackChunkName: "customer-threat-radar" */ "../pages/CustomerThreatRadarPublic")
);

// Phase 5 Sprint 6 — Public service status / uptime.
const StatusPage = lazy(() =>
  import(/* webpackChunkName: "status" */ "../pages/Status")
);
// Country-shortcut redirect — /r/<iso2> → /threat-radar with pre-pan params
const RegionRedirect = lazy(() =>
  import(/* webpackChunkName: "region-redirect" */ "../pages/RegionRedirect")
);

// Phase 5.5 — QorTrace Labs sub-brand site (also served at labs.qortrace.com).
const Labs = lazy(() =>
  import(/* webpackChunkName: "labs", webpackPrefetch: true */ "../pages/Labs")
);
// Iter248 — Public Wall of Shipped (curated Track B social archive).
const WallOfShipped = lazy(() =>
  import(/* webpackChunkName: "wall-of-shipped" */ "../pages/WallOfShipped")
);
const LabsService = lazy(() =>
  import(/* webpackChunkName: "labs-service" */ "../pages/LabsService")
);
const LabsManifesto = lazy(() =>
  import(/* webpackChunkName: "labs-manifesto" */ "../pages/LabsManifesto")
);
const LabsMethodology = lazy(() =>
  import(/* webpackChunkName: "labs-methodology" */ "../pages/LabsMethodology")
);
const LabsEngagements = lazy(() =>
  import(/* webpackChunkName: "labs-engagements" */ "../pages/LabsEngagements")
);
const LabsInsight = lazy(() =>
  import(/* webpackChunkName: "labs-insight" */ "../pages/LabsInsight")
);
const LabsVerify = lazy(() =>
  import(/* webpackChunkName: "labs-verify" */ "../pages/LabsVerify")
);
const LabsPlaybook = lazy(() =>
  import(/* webpackChunkName: "labs-playbook" */ "../pages/LabsPlaybook")
);
// Iter268 — MPC Vendor Matrix (productised subscription).
const MpcVendorMatrix = lazy(() =>
  import(/* webpackChunkName: "mpc-vendor-matrix" */ "../pages/MpcVendorMatrix")
);
const ContractSigningPortal = lazy(() =>
  import(/* webpackChunkName: "contract-signing" */
         "../pages/ContractSigningPortal")
);

// Iter270 — Pricing IA rebuild: hub + enterprise + monitoring pages.
const PricingHub = lazy(() =>
  import(/* webpackChunkName: "pricing-hub" */ "../pages/PricingHub")
);
const PricingEnterprise = lazy(() =>
  import(/* webpackChunkName: "pricing-enterprise" */ "../pages/PricingEnterprise")
);
const PricingMonitoring = lazy(() =>
  import(/* webpackChunkName: "pricing-monitoring" */ "../pages/PricingMonitoring")
);

// Iter279 — Track A PR amplification layer
const ExposedWallets = lazy(() =>
  import(/* webpackChunkName: "exposed-wallets" */ "../pages/ExposedWallets")
);
const BulkScan = lazy(() =>
  import(/* webpackChunkName: "bulk-scan" */ "../pages/BulkScan")
);

// Phase 10 iter 4 — counsel-facing matter view (token-gated)
const LegalCounselView = lazy(() =>
  import(/* webpackChunkName: "legal-counsel" */ "../pages/LegalCounselView")
);

// M3a — QorBOM partner-application landing (sister-brand B2B funnel).
// Temporary home inside QorTrace frontend until M3b ships the dedicated
// qorbom.com site. Has its own Light + Cobalt visual identity, no
// QorTrace Header/Footer.
const BecomeAQorbomPartner = lazy(() =>
  import(/* webpackChunkName: "qorbom-partners" */ "../pages/BecomeAQorbomPartner")
);

// M3a Step 2 — QorBOM partner API developer reference + OpenAPI 3.1
// Phase 14.5 — Migrated from a single-file page to a 3-column Stripe/
// Mintlify-style docs shell with 6 lazy-loaded sub-pages. The original
// `QorbomApiDocs.jsx` is now retained only as a redirect helper so
// inbound deep-links don't 404 during cache transitions.
const QorbomApiDocsRedirect = lazy(() =>
  import(/* webpackChunkName: "qorbom-api-docs-redirect" */ "../pages/QorbomApiDocs")
);
const QorbomDocsQuickstart = lazy(() =>
  import(/* webpackChunkName: "qorbom-docs-quickstart", webpackPrefetch: true */ "../pages/qorbom-docs/QorbomDocsQuickstart")
);
const QorbomDocsAuth = lazy(() =>
  import(/* webpackChunkName: "qorbom-docs-auth" */ "../pages/qorbom-docs/QorbomDocsAuth")
);
const QorbomDocsEndpoints = lazy(() =>
  import(/* webpackChunkName: "qorbom-docs-endpoints" */ "../pages/qorbom-docs/QorbomDocsEndpoints")
);
const QorbomDocsWebhooks = lazy(() =>
  import(/* webpackChunkName: "qorbom-docs-webhooks" */ "../pages/qorbom-docs/QorbomDocsWebhooks")
);
const QorbomDocsErrors = lazy(() =>
  import(/* webpackChunkName: "qorbom-docs-errors" */ "../pages/qorbom-docs/QorbomDocsErrors")
);
const QorbomDocsSdks = lazy(() =>
  import(/* webpackChunkName: "qorbom-docs-sdks" */ "../pages/qorbom-docs/QorbomDocsSdks")
);
// Phase 14 Day-2 — Scalar-rendered API reference (auto from OpenAPI spec)
const QorbomApiReference = lazy(() =>
  import(/* webpackChunkName: "qorbom-api-reference" */ "../pages/QorbomApiReference")
);
// Phase 14 Day-6 — Partner self-service portal
const QorbomPartnerPortal = lazy(() =>
  import(/* webpackChunkName: "qorbom-portal" */ "../pages/QorbomPartnerPortal")
);
// QorBOM trust & compliance page — for procurement teams
const QorbomTrust = lazy(() =>
  import(/* webpackChunkName: "qorbom-trust" */ "../pages/QorbomTrust")
);
// Phase 14.3 — QorBOM marketing landing for qorbom.com hostname.
const QorbomHomepage = lazy(() =>
  import(/* webpackChunkName: "qorbom-home" */ "../pages/QorbomHomepage")
);
// Phase 14 Day-7 — tenant-slug white-label landing for {slug}.qorbom.com
const QorbomTenantLanding = lazy(() =>
  import(/* webpackChunkName: "qorbom-tenant-landing" */ "../pages/QorbomTenantLanding")
);


/**
 * BrandAwareRoot — `/` is rendered differently per hostname:
 *
 *   {slug}.qorbom.com  → QorbomTenantLanding (white-label partner page)
 *   qorbom.com (apex)  → QorbomHomepage      (B2B marketing site)
 *   anywhere else      → Landing             (QorTrace primary brand)
 *
 * SSR-safe (returns Landing during prerender, swap happens client-
 * side as soon as `window` is available).
 */
function BrandAwareRoot() {
  if (typeof window === "undefined") return <Landing />;
  const host = (window.location.hostname || "").toLowerCase();
  // Phase 14.6 — app.qorbom.com hosts the Partner Portal SPA. Root
  // path lands on the overview tab; deep-link paths (/keys, /scans,
  // /branding) are handled via explicit routes below — the portal
  // itself reads window.location.pathname to pick the initial tab.
  if (host === "app.qorbom.com") {
    return <QorbomPartnerPortal />;
  }
  // Phase 14.7 — status.qorbom.com renders the public service-health
  // page. Same component as qortrace.com/status — the page reads its
  // own hostname to swap brand chrome (QorBOM nav/footer instead of
  // QorTrace) so visitors arriving via the QorBOM subdomain see the
  // QorBOM brand experience.
  if (host === "status.qorbom.com") {
    return <StatusPage />;
  }
  // Tenant subdomain pattern: a single label in front of qorbom.com,
  // BUT skip reserved operational labels (www, docs, status, api, etc.)
  // — those should fall through to the apex marketing homepage.
  // Mirrors backend _RESERVED_SUBDOMAINS in cbom_routes.py.
  const tenantMatch = host.match(/^([a-z0-9][a-z0-9-]{1,30}[a-z0-9]?)\.qorbom\.com$/i);
  if (tenantMatch && !QORBOM_RESERVED_SUBDOMAINS.has(tenantMatch[1])) {
    return <QorbomTenantLanding />;
  }
  if (/(?:^|\.)qorbom\.com$/i.test(host)) {
    return <QorbomHomepage />;
  }
  return <Landing />;
}

// Subdomains that should serve the QorBOM apex marketing page instead of
// being interpreted as a tenant slug. Keep in sync with
// backend/cbom_routes.py::_RESERVED_SUBDOMAINS.
const QORBOM_RESERVED_SUBDOMAINS = new Set([
  "www", "api", "app", "mail", "admin", "auth", "login", "signup",
  "billing", "docs", "status", "support", "help", "blog", "press",
  "trust", "security", "static", "assets", "cdn", "img", "images",
  "media", "files", "download", "downloads", "ws", "websocket",
  "smtp", "imap", "pop", "ftp", "ssh", "vpn", "ns1", "ns2",
  "monitor", "metrics", "logs", "stats", "console", "dashboard",
  "portal", "go", "links", "track", "tracking", "email",
  "noreply", "no-reply", "hello", "team", "partners", "partner",
  "internal", "qortrace", "qorbom", "qor", "test", "staging",
  "preview", "demo", "sandbox", "dev", "development", "prod",
  "production", "qa", "uat", "developers",
]);


const publicRoutes = [
  { path: "/", element: <BrandAwareRoot /> },
  // Direct test/preview route for QorbomHomepage so we can hit it
  // from the preview URL without spoofing the hostname.
  { path: "/qorbom-home", element: <QorbomHomepage /> },
  // Preview-only test route for tenant landings. In production
  // these render at `{slug}.qorbom.com/` via BrandAwareRoot.
  { path: "/qorbom-tenant-preview", element: <QorbomTenantLanding /> },
  { path: "/account/contracts/:contractId", element: <ContractSigningPortal /> },
  { path: "/scan", element: <ScanPage /> },
  { path: "/scan/bulk", element: <BulkScan /> },
  { path: "/threat-radar/exposed-wallets", element: <ExposedWallets /> },
  { path: "/contract", element: <ContractPage /> },
  { path: "/atlas", element: <AtlasPage /> },
  { path: "/methodology", element: <MethodologyPage /> },
  { path: "/methodology/:slug", element: <MethodologyVersionPage /> },
  { path: "/verify", element: <VerifyPage /> },
  { path: "/verify/:auditId", element: <VerifyPage /> },
  // Iter270 — Pricing IA rebuild.
  // /pricing → decision page (the new PricingHub).
  // /pricing/scanner → the original Pricing component (scanner SaaS tiers).
  // /pricing/enterprise → PQC Audit Suite primary home.
  // /pricing/monitoring → Atlas + MPC Vendor Matrix.
  // /pricing/audits is preserved for backwards-compat — it now redirects
  // its #pqc-suite traffic through to /pricing/enterprise via the Suite
  // configurator embedded at both places.
  { path: "/pricing", element: <PricingHub /> },
  { path: "/pricing/scanner", element: <Pricing /> },
  { path: "/pricing/enterprise", element: <PricingEnterprise /> },
  { path: "/pricing/monitoring", element: <PricingMonitoring /> },
  { path: "/free-scan", element: <FreeScan /> },
  { path: "/quickstart", element: <PublicQuickstart /> },
  { path: "/score-my-contract", element: <ScoreMyContractRedirect /> },
  { path: "/scan/result/:scanId", element: <ScanResult /> },
  { path: "/audit", element: <Audit /> },
  { path: "/audit/preview", element: <AuditPreview /> },
  { path: "/audit/talk-to-sales", element: <AuditTalkToSales /> },
  { path: "/audit/billing/success", element: <AuditCheckoutSuccess /> },
  { path: "/demo/checkout-success", element: <DemoCheckoutSuccess /> },
  { path: "/d/:token", element: <DemoShareRedeem /> },
  { path: "/legal/counsel/:token", element: <LegalCounselView /> },
  { path: "/pricing/audits", element: <PricingAudits /> },
  { path: "/signin", element: <SignIn /> },
  { path: "/signup", element: <SignIn /> },
  { path: "/account/forgot-password", element: <ForgotPassword /> },
  { path: "/account/reset-password",  element: <ResetPassword /> },
  { path: "/unsubscribe", element: <Unsubscribe /> },
  { path: "/tools/verify-webhook", element: <VerifyWebhook /> },
  // Phase 2b — public docs surface. /docs is the IA landing, /docs/* renders
  // any published wiki page (including nested slugs like internal/team-runbook).
  { path: "/docs", element: <DocsLanding /> },
  { path: "/docs/sdks", element: <SdkSamples /> },
  { path: "/docs/*", element: <DocsArticle /> },
  // Iter269.19a — `/changelog` top-level redirect to the docs changelog
  // page. SaaS standard short URL; previously only available at
  // `/docs/changelog`.
  { path: "/changelog", element: <Navigate to="/docs/changelog" replace /> },
  // Phase 9 (iter269.20m) — Finance & Partnership Deck.
  { path: "/deck", element: <Deck /> },
  // Iter269.20r — Multi-deck CMS render route.
  { path: "/deck/:slug", element: <PublicDeck /> },
  // Phase 8.1 — Help Center hub.
  { path: "/help", element: <HelpCenter /> },
  { path: "/help/:category", element: <HelpCategory /> },
  // Phase 8.4 — Public Community Pulse.
  { path: "/trends", element: <PublicTrends /> },
  // Iter270.1 — Public momentum dashboard (anonymized scan velocity).
  { path: "/momentum", element: <Momentum /> },
  // Iter270.1 — Public scan comparator (two scans, side-by-side).
  { path: "/compare", element: <Compare /> },
  // Iter276 — Programmatic SEO chain landing pages. 18 chains supported.
  { path: "/chains/:slug", element: <ChainLanding /> },
  // Phase 4 — Public Trust & Compliance Portal.
  { path: "/trust", element: <Trust /> },
  { path: "/trust/policies/:slug", element: <TrustPolicyView /> },
  { path: "/trust/audit-updates/unsubscribe",
    element: <TrustAuditUpdatesUnsubscribe /> },
  // Phase 12.6 — Standalone Threat Model page (auditor-facing).
  { path: "/threat-model", element: <ThreatModel /> },
  { path: "/sister-brands", element: <SisterBrands /> },
  // Phase 15.1 / M5 — Q-Day Tax Clock + embeddable widget.
  { path: "/q-day", element: <QDayClock /> },
  { path: "/widget/q-day-counter", element: <QDayCounterWidget /> },
  // Phase 12.7c — Public Release log.
  { path: "/releases", element: <Releases /> },
  { path: "/status", element: <StatusPage /> },
  { path: "/r/:code", element: <RegionRedirect /> },
  // Phase 5.5 — QorTrace Labs (sub-brand). Lives at /labs and labs.qortrace.com.
  { path: "/labs", element: <Labs /> },
  { path: "/labs/wall-of-shipped", element: <WallOfShipped /> },
  { path: "/labs/manifesto", element: <LabsManifesto /> },
  { path: "/labs/methodology", element: <LabsMethodology /> },
  { path: "/labs/engagements", element: <LabsEngagements /> },
  { path: "/labs/insights/:slug", element: <LabsInsight /> },
  { path: "/labs/services/:slug", element: <LabsService /> },
  { path: "/labs/mpc-vendor-matrix", element: <MpcVendorMatrix /> },
  { path: "/labs/verify/:certId", element: <LabsVerify /> },
  { path: "/labs/playbook/:token", element: <LabsPlaybook /> },
  // Phase 5 Sprint 4+5 — Public Threat Radar.
  { path: "/threat-radar", element: <ThreatRadar /> },
  { path: "/threat-radar/embed", element: <ThreatRadarEmbed /> },
  { path: "/threat-radar/stats", element: <ThreatRadarStats /> },
  // Iter203 — Customer-curated public PQC threat radars.
  { path: "/customer/:slug/threat-radar", element: <CustomerThreatRadarPublic /> },
  { path: "/whats-new", element: <WhatsNew /> },
  { path: "/report/:id", element: <ReportPage /> },
  { path: "/legal/terms", element: <Terms /> },
  { path: "/legal/privacy", element: <Privacy /> },
  { path: "/legal/cookies", element: <CookiesPolicy /> },
  { path: "/legal/security", element: <Security /> },
  // M3a — QorBOM sister-brand partner-application landing.
  //   • On qortrace.com:  served under `/qorbom/*` (legacy paths).
  //   • On qorbom.com:    served at the bare path (the CF Worker
  //     in /app/cloudflare/qorbom-router.js proxies qorbom.com/*
  //     to qortrace.com/*; the React app detects
  //     `window.location.hostname === 'qorbom.com'` and mounts
  //     these routes at root).
  { path: "/qorbom/become-a-partner", element: <BecomeAQorbomPartner /> },
  // Phase 14.5 — QorBOM docs 3-column Stripe/Mintlify shell.
  // Hitting `/qorbom/api-docs` (no sub-path) lands on Quickstart per
  // the user-confirmed scope. Old single-file route still mounts the
  // legacy component as a fallback during cache transitions.
  { path: "/qorbom/api-docs",                element: <QorbomDocsQuickstart /> },
  { path: "/qorbom/api-docs/quickstart",     element: <QorbomDocsQuickstart /> },
  { path: "/qorbom/api-docs/auth",           element: <QorbomDocsAuth /> },
  { path: "/qorbom/api-docs/endpoints",      element: <QorbomDocsEndpoints /> },
  { path: "/qorbom/api-docs/webhooks",       element: <QorbomDocsWebhooks /> },
  { path: "/qorbom/api-docs/errors",         element: <QorbomDocsErrors /> },
  { path: "/qorbom/api-docs/sdks",           element: <QorbomDocsSdks /> },
  // Catch-all under `/qorbom/api-docs/*` — anything else lands on
  // Quickstart (the docs index) so deep links that pre-dated the
  // re-shell don't 404.
  { path: "/qorbom/api-docs/*",              element: <QorbomDocsQuickstart /> },
  { path: "/qorbom/api-reference",    element: <QorbomApiReference /> },
  { path: "/qorbom/portal",           element: <QorbomPartnerPortal /> },
  { path: "/qorbom/trust",            element: <QorbomTrust /> },
  // Phase 14.2 — qorbom.com hostname root paths. These render the
  // same Qorbom* components without the `/qorbom` prefix so the
  // URL bar reads `qorbom.com/become-a-partner` etc.
  { path: "/become-a-partner",        element: <BecomeAQorbomPartner /> },
  // Phase 14.5 — same 3-column docs at the qorbom.com hostname root.
  { path: "/developers",              element: <QorbomDocsQuickstart /> },
  { path: "/developers/quickstart",   element: <QorbomDocsQuickstart /> },
  { path: "/developers/auth",         element: <QorbomDocsAuth /> },
  { path: "/developers/endpoints",    element: <QorbomDocsEndpoints /> },
  { path: "/developers/webhooks",     element: <QorbomDocsWebhooks /> },
  { path: "/developers/errors",       element: <QorbomDocsErrors /> },
  { path: "/developers/sdks",         element: <QorbomDocsSdks /> },
  { path: "/developers/*",            element: <QorbomDocsQuickstart /> },
  { path: "/api-reference",           element: <QorbomApiReference /> },
  { path: "/portal",                  element: <QorbomPartnerPortal /> },
  // Phase 14.6 — Partner portal at app.qorbom.com. The Cloudflare
  // Worker proxies `app.qorbom.com/<path>` to qortrace.com/portal/<path>,
  // but the browser URL bar stays at app.qorbom.com/<path> — so the
  // SPA needs routes at the original paths to map deep-links to the
  // correct tab. BrandAwareRoot above handles the root path; these
  // handle the named tabs. The portal reads `window.location.pathname`
  // to set its initial tab.
  { path: "/overview",                element: <QorbomPartnerPortal /> },
  { path: "/keys",                    element: <QorbomPartnerPortal /> },
  { path: "/scans",                   element: <QorbomPartnerPortal /> },
  { path: "/branding",                element: <QorbomPartnerPortal /> },
  // Legacy single-file route kept for 30 days during cache transition.
  // After 2026-06-30 this can be removed; the catch-all `/qorbom/api-docs/*`
  // above already covers any inbound stragglers.
  { path: "/qorbom/api-docs-legacy",  element: <QorbomApiDocsRedirect /> },
];

export default publicRoutes;
